import Pagination from './Pagination'
import Spinner from './Spinner'
import LoadingOverlay from './LoadingOverlay'
import { headerLabel } from '../utils/columns'

const CustomTable = ({
  columns,
  data,
  rowKey,
  onRowClick,
  sortColumn,
  sortDirection = 'asc',
  onSort,
  pagination,
  headerBg = '#e8ecf3',
  textColor = '#252a34',
  rowBorderColor = '#dfe4ed',
  bordered = false,
  stickyHeader = false,
  isRowSelected,
  isLoading = false,
  error = null,
  onRetry,
  emptyMessage = 'No records found',
  loadingLabel,
  fitColumnsToContent = false,
}) => {

  // fitColumnsToContent: for the wide data tables, whose headers carry a unit
  // and are the widest thing in most columns. Auto layout lets each column size
  // to its own header instead of truncating it at a fixed width; col.width
  // becomes a floor so the narrow ones don't collapse. Off by default — the
  // list-style tables (VIM, BMS, nodes) are laid out by their fixed widths.
  const isEmpty = data.length === 0
  const isFirstLoad = isLoading && isEmpty
  const isRefetching = isLoading && !isEmpty
  const showError = !isLoading && isEmpty && Boolean(error)

  // Rendered as a sibling of the scroll container, not as a `colSpan` row inside
  // the table: a wide column set makes the table several times the viewport
  // width, so a centered table cell puts this halfway off screen and the user
  // has to scroll sideways to find out there's no data.
  const emptyState = showError ? (
    <div className="flex flex-col items-center gap-2">
      <span className="material-icons text-2xl text-[#d92d20]">error_outline</span>
      <span className="text-sm font-medium text-[#272d37]">{error}</span>
      {onRetry && (
        <button
          className="mt-1 h-8 px-3 border border-[#dfe4ed] rounded-md bg-white cursor-pointer text-sm text-[#272d37]"
          onClick={onRetry}
        >
          Retry
        </button>
      )}
    </div>
  ) : (
    <span className="text-sm font-normal text-[#616a79]">{emptyMessage}</span>
  )

  return (
    <div
      className={`flex-1 flex flex-col w-full min-h-px overflow-hidden ${bordered ? 'bg-[#f9fafa] rounded-lg border border-[#dfe4ed]' : ''}`}
    >
      {isFirstLoad ? (
        <Spinner label={loadingLabel} />
      ) : (
        <div className="relative flex-1 flex flex-col min-h-px">
        {isRefetching && <LoadingOverlay label={loadingLabel} />}
        <div
          className={`flex-1 flex flex-col overflow-auto transition-opacity ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
          aria-busy={isLoading}
        >
          <table className={`min-w-full border-collapse ${fitColumnsToContent ? 'table-auto' : 'table-fixed'}`}>
            <thead>
              <tr className={stickyHeader ? 'sticky top-0 z-10' : ''} style={{ backgroundColor: headerBg }}>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    title={headerLabel(col)}
                    style={{
                      color: textColor,
                      ...(col.width ? (fitColumnsToContent ? { minWidth: col.width } : { width: col.width }) : {}),
                    }}
                    className={`px-4 py-2.5 text-sm font-medium border-b border-[#dfe4ed] whitespace-nowrap ${fitColumnsToContent ? '' : 'overflow-hidden text-ellipsis'} ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    {col.sortable ? (
                      <button
                        className="inline-flex max-w-full items-center gap-1 bg-transparent border-none cursor-pointer text-sm font-medium p-0"
                        style={{ color: textColor }}
                        onClick={() => onSort?.(col.key)}
                      >
                        <span className={`flex-1 min-w-0 whitespace-nowrap text-left ${fitColumnsToContent ? '' : 'overflow-hidden text-ellipsis'}`}>
                          {headerLabel(col)}
                        </span>
                        <span className="material-icons text-base text-[#006c4d] shrink-0">
                          {sortColumn === col.key && sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                        </span>
                      </button>
                    ) : (
                      headerLabel(col)
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                  <tr
                    key={rowKey ? rowKey(row, i) : i}
                    className={`bg-[#f9fafa] cursor-pointer ${isRowSelected?.(row) ? 'outline outline-2 -outline-offset-2 outline-[#069729]' : ''}`}
                    style={{ borderBottom: `1px solid ${rowBorderColor}` }}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => {
                      const content = col.render ? col.render(row) : row[col.key]
                      const isEmptyCell = content === undefined || content === null || content === ''
                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-3 text-sm font-normal whitespace-nowrap ${fitColumnsToContent ? '' : 'overflow-hidden text-ellipsis'} ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                          style={{ color: textColor }}
                        >
                          {/* Under auto layout a cell grows to its content, so
                              one long value would stretch its column and shove
                              the rest off screen. The cap has to sit on a block
                              box inside the cell — max-width on a td isn't
                              honored there. */}
                          {fitColumnsToContent ? (
                            <span className="block max-w-[320px] overflow-hidden text-ellipsis">
                              {isEmptyCell ? '--' : content}
                            </span>
                          ) : (
                            isEmptyCell ? '--' : content
                          )}
                        </td>
                      )
                    })}
                  </tr>
              ))}
            </tbody>
          </table>
          {/* A flex child of the scroll container, so it's sized to the visible
              width rather than to the table's — a centered `colSpan` cell put
              this halfway across a 3000px-wide column set, off screen. sticky
              left-0 holds it in place while the columns scroll under it. */}
          {isEmpty && (
            <div className="sticky left-0 flex-1 flex items-center justify-center px-4 py-10">
              {emptyState}
            </div>
          )}
        </div>
        </div>
      )}

      {pagination && <Pagination {...pagination} />}
    </div>
  )
}

export default CustomTable
